import {
    createLockerRestorer,
    lockerInvocation,
    type LockerPrepareRequest,
    type LockerReady,
    type LockerResolution,
    type LockerSigned,
} from '@aquariusdefi/sdk';
import { rpc, Transaction, TransactionBuilder } from '@stellar/stellar-sdk-15';

import { ENV_TESTNET } from 'constants/env';
import { CONTRACTS } from 'constants/soroban';
import { NETWORK_PASSPHRASES } from 'constants/stellar';

import { getNetworkPassphrase } from 'helpers/env';

import type { LockerPreparationFactory } from './lockerPreparation';
import { proxyInvocation } from './proxyPreparation';

type Server = Parameters<typeof createLockerRestorer>[0];
const now = () => BigInt(Math.floor(Date.now() / 1000));

const buildInvocation = (request: LockerPrepareRequest, passphrase: string) => {
    if (
        request.contract !== CONTRACTS.testnet.tokenTimelock &&
        ['delegate', 'undelegate', 'batch'].includes(request.calls[0]?.method)
    ) {
        return proxyInvocation(request, passphrase);
    }
    if (
        passphrase !== NETWORK_PASSPHRASES[ENV_TESTNET] ||
        request.contract !== CONTRACTS.testnet.tokenTimelock ||
        request.source !== request.owner ||
        (request.calls.length > 1 && request.batcher !== CONTRACTS.testnet.batch) ||
        !(
            (request.calls.length === 1 && request.calls[0].method === 'create_lock') ||
            (request.calls.length > 0 && request.calls.every(call => call.method === 'withdraw'))
        )
    ) {
        throw new Error('Unsupported Locker request');
    }
    return lockerInvocation(request.contract, request.owner, request.calls, request.batcher)
        .operation;
};

// RPC simulation supplies the current footprint, resources, auth and resource fee.
// No release configuration hash or measured resource class gates this path.
export const createNetworkLockerPreparationFactory = (options: {
    createServer: (url: string) => Server;
    fee: string;
}): LockerPreparationFactory => ({
    assertAvailable: request =>
        Promise.resolve().then(() => {
            buildInvocation(request, getNetworkPassphrase());
        }),
    create: context => {
        const server = options.createServer(context.rpcUrl);
        const readyTransactions = new WeakSet<LockerReady>();
        const signedTransactions = new WeakSet<LockerSigned>();
        const attempted = new WeakSet<LockerSigned>();
        const restore = createLockerRestorer(server, {
            networkPassphrase: context.networkPassphrase,
            fee: options.fee,
            sign: envelope => {
                if (!context.signRestore)
                    throw new Error('Connect the Locker wallet before restoring state.');
                return context.signRestore(envelope);
            },
        });
        const reconcile = async (signed: LockerSigned): Promise<LockerResolution> => {
            if (!signedTransactions.has(signed)) throw new Error('Unknown Locker signed envelope');
            try {
                const result = await server.getTransaction(signed.hash);
                if (result.status === 'SUCCESS') {
                    signedTransactions.delete(signed);
                    return {
                        status: 'success',
                        hash: signed.hash,
                        returnValueXdr: result.returnValue?.toXDR('base64'),
                    };
                }
                if (result.status === 'FAILED') {
                    signedTransactions.delete(signed);
                    return { ...signed, status: 'failed' };
                }
            } catch {
                /* Keep the original hash when RPC cannot establish the result. */
            }
            return { ...signed, status: 'unknown' };
        };
        return {
            prepare: async request => {
                const operation = buildInvocation(request, context.networkPassphrase);
                if (request.source !== context.source)
                    throw new Error('Locker source changed; prepare again');
                for (let attempt = 0; attempt < 3; attempt += 1) {
                    const account = await server.getAccount(request.source);
                    const tx = new TransactionBuilder(account, {
                        fee: options.fee,
                        networkPassphrase: context.networkPassphrase,
                    })
                        .addOperation(operation)
                        .setTimeout(720)
                        .build();
                    const simulation = await server.simulateTransaction(tx);
                    if (rpc.Api.isSimulationError(simulation)) throw new Error(simulation.error);
                    if (!rpc.Api.isSimulationSuccess(simulation))
                        throw new Error('Unsupported Locker simulation');
                    const data = simulation.transactionData.build();
                    const archived =
                        data.ext().switch() === 1 &&
                        data.ext().resourceExt().archivedSorobanEntries().length > 0;
                    if (rpc.Api.isSimulationRestore(simulation) || archived) {
                        if (context.purpose === 'preview')
                            throw new Error(
                                'Locker state needs restoration. Prepare the withdrawal before previewing it again.',
                            );
                        await restore(request, simulation);
                        continue;
                    }
                    const prepared = rpc.assembleTransaction(tx, simulation).build();
                    const ready = Object.freeze({
                        envelopeXdr: prepared.toXDR(),
                        hash: prepared.hash().toString('hex'),
                        signBy: prepared.timeBounds.maxTime,
                        maxTime: prepared.timeBounds.maxTime,
                        simulationLedger: simulation.latestLedger,
                    });
                    readyTransactions.add(ready);
                    return ready;
                }
                throw new Error('Locker restoration requires fresh preparation. Please retry.');
            },
            sign: async (ready, signer, clock = now) => {
                if (!readyTransactions.delete(ready))
                    throw new Error('Unknown or already signed Locker transaction');
                if (clock() >= BigInt(ready.signBy))
                    throw new Error('Locker signing deadline elapsed; prepare again');
                const signed = new Transaction(
                    await signer(ready.envelopeXdr),
                    context.networkPassphrase,
                );
                if (clock() >= BigInt(ready.signBy))
                    throw new Error('Locker signing deadline elapsed; prepare again');
                if (signed.hash().toString('hex') !== ready.hash)
                    throw new Error('Wallet changed prepared Locker transaction');
                if (!signed.signatures.length)
                    throw new Error('Wallet returned unsigned Locker transaction');
                const result = Object.freeze({ envelopeXdr: signed.toXDR(), hash: ready.hash });
                signedTransactions.add(result);
                return result;
            },
            submit: async signed => {
                if (!signedTransactions.has(signed))
                    throw new Error('Unknown Locker signed envelope');
                if (attempted.has(signed)) return reconcile(signed);
                attempted.add(signed);
                try {
                    const response = await server.sendTransaction(
                        new Transaction(signed.envelopeXdr, context.networkPassphrase),
                    );
                    if (
                        response.status === 'ERROR' &&
                        response.errorResult?.result().switch().value < 0
                    ) {
                        signedTransactions.delete(signed);
                        return {
                            ...signed,
                            status:
                                response.errorResult.result().switch().name === 'txBadSeq'
                                    ? 'rebuild-required'
                                    : 'failed',
                        };
                    }
                } catch {
                    /* A failed send request can still have reached the network. */
                }
                return reconcile(signed);
            },
            reconcile,
        };
    },
});
